import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { purchaseKey, ratesKey } from "./entities";
import { fetchBillingSummary, rateById, ratesMap } from "./rates.slice";
import { selectFacilityEntities } from "./facilities.slice";
import { fetchBillingProfiles, selectBillingProfileEntities } from "./billingProfiles.slice";
import { allSurveyQuestions } from "./surveyQuestions.slice";
import { get, has, isArray, isEmpty, union, omit } from 'lodash';
import { selectAccessDeviceById, selectAccessDeviceEntities } from "./accessDevices.slice";
import { callAuthAPI, callGraphQL, callMyProfile } from "./api.slice";

const searchParams = new URLSearchParams(window.location.search);
const default_rate_id = searchParams.has('rate_id') ? searchParams.get('rate_id') : '';
const default_start_date = searchParams.has('start_date') ? searchParams.get('start_date') : '';
const default_promo_code = searchParams.has('promo_code') ? searchParams.get('promo_code') : '';
const default_facility_id = searchParams.has('facility_id') ? searchParams.get('facility_id') : '';
const default_promo_code_only = searchParams.has('promoCodeOnly') ? searchParams.get('promoCodeOnly') : 'false';

export const setParkingSpot = createAsyncThunk(`${purchaseKey}/setParkingSpot`, async ({ id, rateId, startDate, promoCode }, context) => {
    const state = context.getState();
    const purchase = selectPurchase(state);
    const { billing_profile_id, access_devices } = rateById(state, rateId);
    const requiredAccessDevices = access_devices.map(adId => selectAccessDeviceById(state, adId))
        .filter(({ required }) => !!required)
        .map(({ id: adId }) => adId)
    if (!purchase.emptyForm.rateId) context.dispatch(setRateId(rateId));
    if (!purchase.emptyForm.promoCode) context.dispatch(setPromoCode(promoCode));
    if (!purchase.billingProfileId) context.dispatch(setBillingProfileId(billing_profile_id))
    return { id, rateId, startDate, promoCode, requiredAccessDevices };
});

export const setParkingData = createAsyncThunk(`${purchaseKey}/setParkerData`, async ({ id, ...values }, context) => {
    const state = selectPurchase(context.getState());
    if (!has(state, `parkers.${id}`)) return context.rejectWithValue('Parker id is not valid');
    return context.dispatch(setParker({ id, ...values }));
});

export const setPayorData = createAsyncThunk(`${purchaseKey}/setPayorData`, async ({ ...values }) => {
    return values;
})

export const savePaymentToken = createAsyncThunk(`${purchaseKey}/savePaymentToken`, async ({ gpi_token, gpi_bank, cardInfo }, context) => {
    const useGpiCard = !!gpi_token;
    const useGpiBank = !!gpi_bank;
    const query = useGpiBank ? 'mutation savePaymentToken($token: GPIBankAccountInput!) { savePaymentToken(gpi_bank_account: $token) }' :
        'mutation savePaymentToken($token: GPIInput!) { savePaymentToken(gpi_token: $token) }';
    const { type, payload, error } = await context.dispatch(callGraphQL({
        query: query,
        variables: { token: gpi_token || gpi_bank },
        response: { savePaymentToken: String }
    }));

    if (type.endsWith('rejected')) {
        return context.rejectWithValue(payload || error);
    }

    if (useGpiCard) {
        return context.dispatch(setNewCard({ token: payload.savePaymentToken, cardInfo }));
    } else if (useGpiBank) {
        const bankAccount = {
            ...gpi_bank,
            token: payload.savePaymentToken
        };
        return context.dispatch(setNewGPIBank(bankAccount));
    } else {
        return context.dispatch(setPaymentMethodId(payload.savePaymentToken));
    }
});
export const callPurchaseParking = createAsyncThunk(`${purchaseKey}/callPurchaseParking`, async (action, context) => {
    const {
        facilityId,
        parkers,
        paymentData,
        payorData,
        surveyResponses,
        subscriptionId
    } = selectPurchase(context.getState());

    const accessDevicesMap = selectAccessDeviceEntities(context.getState());

    const purchaseInput = parkers.reduce((input, parker) => {
        const {
            rateId,
            promoCode,
            first_name,
            last_name,
            email,
            phone,
            address,
            city,
            state,
            zip,
            vehicles,
            accessDevices,
            startDate
        } = parker;

        const parkerAccessDevices = accessDevices.map(ad => {
            const accessDevice = accessDevicesMap[ad];
            return { type: accessDevice.type.toUpperCase().replaceAll(' ', '_') }; // Convert for api v1 graphql enum
        });

        const parkerData = {
            email,
            phone,
            address,
            city,
            state,
            zip,
            vehicles,
            first_name,
            last_name,
            start_date: startDate,
            access_devices: parkerAccessDevices,
        };

        const rateInput = input.find(i => i.rate_id === rateId);
        if (rateInput) {
            rateInput.parkers.push(parkerData);
        } else {
            input.push({ rate_id: rateId, promo_code: promoCode, parkers: [parkerData] })
        }

        return input;
    }, []);

    const { auto_pay, payment_method_id } = paymentData;
    const variables = {
        facility_id: facilityId,
        subscription: {
            id: subscriptionId,
            auto_pay,
            payment_method_id,
            survey_responses: surveyResponses
        },
        rate_purchases: purchaseInput,
        payor: omit(payorData, ['cognito_sub', 'info_name', 'info_attn'])
    }

    const { type, payload, error } = await context.dispatch(callGraphQL({
        query: `mutation purchaseParking($facility_id: String!, $subscription: SubscriptionInput!, $rate_purchases:[PurchaseInput]!, $payor: PayorInput!) {
            purchaseParking(facility_id: $facility_id, subscription: $subscription, rate_purchases: $rate_purchases, payor: $payor) {
                id
                billing_profile {
                    next_steps
                    facility { facility_name }
                }
                payor {
                    type
                    billing_firstname
                    billing_lastname
                    billing_email
                    billing_phone
                    billing_street
                    billing_city
                    billing_state
                    billing_zip
                    info_street
                    info_city
                    info_state
                    info_zip
                }
                parkers {
                    id
                    first_name
                    last_name
                    parker_status_history { action start_date }
                    parkerRateHistory {
                        rate { name }
                    }
                    access_devices { type }
                }
                invoices { 
                    type
                    readable_id
                    created_at
                    payments {
                        type
                        amount
                        payment_meta
                        created_at
                    }
                }
            }
        }`,
        variables,
        response: { purchaseParking: Object }
    }));
    return type.endsWith('rejected') ? context.rejectWithValue(error || payload) : payload.purchaseParking;
});
const slice = createSlice({
    name: purchaseKey,
    initialState: {
        progress: 10,
        billingProfileId: '',
        subscriptionId: null,
        availableRates: [],
        facilityId: default_facility_id,
        promoCode: '',
        promoCodeOnly: default_promo_code_only,
        emptyForm: {
            rateId: default_rate_id,
            promoCode: default_promo_code,
            startDate: default_start_date,
            vehicles: [],
            accessDevices: [],
        },
        parkers: [],
        payorData: {},
        paymentData: {
            type: 'ach',
            auto_pay: true,
            mail_invoice: false,
            payment_method_id: '',
            tax_exempt: false,
            achDisabled: false,
        },
        gpiData: {
            cards: [],
            default_source: null,
            error: null
        },
        gpiBankData: {
            accounts: [],
            default_source: null,
            error: null
        },
        ratesFilter: {
            promoCode: null,
            startDate: null,
        },
        purchaseData: {
            error: null,
            subscription: null
        },
        surveyResponses: [],
        reachedCheckout: false,
    },
    reducers: {
        setParker: (state, action) => {
            const { payload } = action;
            const { id } = payload;
            if (!state.parkers.find(p => p.id === id)) state.parkers.push({ id });
            state.parkers = state.parkers.map(
                parker => parker.id !== id ? parker : { ...parker, ...payload }
            );
        },
        setAccessDevices: (state, action) => {
            const { payload } = action;
            const { id, accessDevices } = payload;
            state.parkers = state.parkers.map(parker => {
                if (parker.id !== id) return parker;
                parker.accessDevices = accessDevices
                return parker;
            })
            return state;
        },
        setBillingProfileId: (state, action) => {
            state.billingProfileId = action.payload;
        },
        setSubscriptionId: (state, action) => {
            state.subscriptionId = action.payload;
        },
        setRateId: (state, action) => {
            state.emptyForm.rateId = action.payload;
        },
        setPromoCode: (state, action) => {
            state.emptyForm.promoCode = action.payload;
        },
        setProgress(state, action) {
            state.progres = action.payload
        },
        setSurveyResponses(state, action) {
            state.surveyResponses = action.payload;
        },
        removeParker(state, action) {
            const { payload } = action;
            const { id } = payload;
            state.parkers = state.parkers.filter(parker => parker.id !== id);
        },
        setPaymentType(state, action) {
            state.paymentData.type = action.payload;
        },
        setPaymentAuto(state, action) {
            state.paymentData.auto_pay = action.payload;
        },
        setPaymentMethodId(state, action) {
            state.paymentData.payment_method_id = action.payload;
        },
        setTaxExempt(state, action) {
            state.paymentData.tax_exempt = action.payload;
        },
        setAchDisabled(state, action) {
            state.paymentData.achDisabled = action.payload;
        },
        setNewCard(state, action) {
            const { token, cardInfo } = action.payload;

            state.paymentData.payment_method_id = token;
            state.gpiData.default_source = token;

            state.gpiData.cards = [
                ...state.gpiData.cards,
                {
                    token,
                    type: cardInfo.type,
                    exp_month: cardInfo.expiry_month,
                    exp_year: cardInfo.expiry_year,
                    last4: cardInfo.masked_card_number.slice(-4),
                }
            ];
        },
        setNewGPIBank(state, action) {
            const bankAccount = action.payload;

            const accountTypeMap = {
                CHECKING: 'Checking',
                SAVINGS: 'Savings',
            };
            const accountType = bankAccount.bank_account_type;
            const accountTypeValue = accountTypeMap[accountType];

            state.gpiBankData.accounts.push({
                token: bankAccount.token,
                accountType: accountTypeValue,
                last4: bankAccount.bank_account_number.slice(-4),
            });
            state.paymentData.payment_method_id = bankAccount.token;
            state.gpiBankData.default_source = bankAccount.token;
        },
        setGPICard(state, action) {
            if (state.gpiData.cards.some(card => card.token === action.payload)) {
                state.gpiData.default_source = action.payload;
                state.paymentData.payment_method_id = action.payload;
            }
        },
        setGPIBank(state, action) {
            if (state.gpiBankData.accounts.some(account => account.token === action.payload)) {
                state.gpiBankData.default_source = action.payload;
                state.paymentData.payment_method_id = action.payload;
            }
        },
        setGPIError(state, action) {
            state.gpiData.error = action.payload;
        },
        setGPIBankError(state, action) {
            state.gpiBankData.error = action.payload;
        },
        setReachedCheckout(state, action) {
            state.reachedCheckout = action.payload;
        }
    },
    extraReducers: {
        [setParkingSpot.fulfilled]: (state, { payload }) => {
            const { id, rateId, startDate, promoCode, requiredAccessDevices } = payload;
            if (!state.parkers.find(p => p.id === id)) state.parkers.push({ id });
            state.parkers = state.parkers.map(parker => {
                if (parker.id !== id) return parker;
                const rateChanged = !isEmpty(parker) && parker.rateId !== payload.rateId
                const accessDevices = rateChanged ? [].concat(requiredAccessDevices)
                    : union(parker.accessDevices, requiredAccessDevices);
                return { ...parker, rateId, startDate, promoCode, requiredAccessDevices, accessDevices };
            })
        },
        [callPurchaseParking.rejected]: (state, { payload }) => {
            state.purchaseData.error = payload;
        },
        [callPurchaseParking.fulfilled]: (state, { payload }) => {
            state.purchaseData.subscription = payload;
        },
        [setPayorData.fulfilled]: (state, { payload }) => {
            const { payorData } = state;
            state.payorData = { ...payorData, ...payload };
        },
        [callMyProfile.fulfilled]: (state, { payload }) => {
            console.log("callMyProfile payload", payload);
            if (payload.gpi_cards) {
                if (payload.gpi_cards.length || !payload.gpi_bank_accounts.length) {
                    state.paymentData.type = 'cc';
                } else {
                    state.paymentData.type = 'ach';
                }
                state.gpiData.cards = payload.gpi_cards;
                state.gpiBankData.accounts = payload.gpi_bank_accounts;
            }

            state.payorData = { ...payload.payor, ...state.payorData };
        },
        [fetchBillingProfiles.fulfilled]: (state, action) => {
            const { meta, payload } = action;
            state.promoCode = meta.arg.promoCode || '';
            state.availableRates = payload[ratesKey] ? Object.keys(payload[ratesKey]) : [];
        },
        [fetchBillingSummary.fulfilled]: (state, action) => {
            const billingSummary = action.payload;
            const initialInvoiceItems = billingSummary.totals.initial.items;
            const hasCreditCardFee = initialInvoiceItems.some(({ type }) => type === 'CREDIT_CARD');

            state.paymentData.hasCreditCardFee = hasCreditCardFee;
        }
    }
});
export const reducer = slice.reducer;
export default reducer;
export const selectPurchase = (state) => state[purchaseKey];
export const getProgress = createSelector(selectPurchase, (purchase) => purchase.progress);
export const getFacilityId = createSelector(selectPurchase, (purchase) => purchase.facilityId)
export const getPromoCodeOnly = createSelector(selectPurchase, (purchase) => purchase.promoCodeOnly)
export const getFacilityInfo = createSelector(
    getFacilityId,
    selectFacilityEntities,
    (facilityId, entities) => get(entities, facilityId, {})
);
export const getParkerInitValues = createSelector(selectPurchase, purchase => purchase.emptyForm)
export const getActivePromoCode = createSelector(
    selectPurchase,
    (purchase) => purchase.promoCode
);
export const getBillingProfileId = () => createSelector(
    selectPurchase,
    (purchase) => purchase.billingProfileId
)
export const getParkingRates = createSelector(
    selectPurchase, ratesMap,
    ({ availableRates, billingProfileId }, rates) => availableRates.map(rid => rates[rid]).filter(
        rate => billingProfileId ? !isEmpty(rate) && get(rate, 'billing_profile_id') === billingProfileId : true
    )
)
export const getAllParkers = createSelector(
    selectPurchase,
    purchase => purchase.parkers
)
export const getParkerById = createSelector(
    getAllParkers,
    (state, parkerId) => parkerId,
    (parkers, parkerId) => parkers.find(({ id }) => id === parkerId)
)
export const getPayorData = createSelector(selectPurchase, purchase => purchase.payorData)
export const getParkerData = (parkers, parkerId) => {
    if (isArray(parkers)) {
        if (parkers.some(parker => parker.id === parkerId)) {
            return parkers.find(parker => parker.id === parkerId);
        } else {
            return {};
        }
    } else get(parkers, parkerId, {});
}

export const getSurveyQuestions = createSelector(
    selectPurchase,
    allSurveyQuestions,
    ({ billingProfileId }, questions) => questions.filter(({ billing_profile_id }) => billing_profile_id === billingProfileId)
)
export const getSurveyResponses = createSelector(selectPurchase, purchase => purchase.surveyResponses);

export const getBillingProfile = createSelector(
    selectPurchase,
    selectBillingProfileEntities,
    ({ billingProfileId }, billingProfiles) => billingProfiles[billingProfileId] || {}
)
export const getPaymentData = createSelector(
    selectPurchase,
    purchase => purchase.paymentData
)
export const getGPIData = createSelector(
    selectPurchase,
    purchase => purchase.gpiData
);
export const getGPIBankData = createSelector(
    selectPurchase,
    purchase => purchase.gpiBankData
);
export const getPurchaseData = createSelector(
    selectPurchase,
    purchase => purchase.purchaseData
)
export const hasReachedCheckout = createSelector(
    selectPurchase,
    (purchase) => !!purchase.reachedCheckout
)

export const getBillingProfileIdValue = createSelector(
    selectPurchase,
    purchase => purchase.billingProfileId
)

export const getSubscriptionIdValue = createSelector(
    selectPurchase,
    purchase => purchase.subscriptionId
)

export const {
    setPromoCode,
    setRateId,
    setParker,
    setAccessDevices,
    setBillingProfileId,
    setSurveyResponses,
    removeParker,
    setPaymentType,
    setPaymentAuto,
    setPaymentMethodId,
    setTaxExempt,
    setReachedCheckout,
    setProgress,
    setGPICard,
    setGPIError,
    setNewCard,
    setNewGPIBank,
    setGPIBank,
    setGPIBankError,
    setSubscriptionId,
    setAchDisabled,
} = slice.actions;
